// WS-SWITCH-INCOMPLETE-1 — the canonical "drop a reply that outlived its
// tenant" writer.
//
// THE CLASS IT CLOSES. Registering a key as workspace-scoped (state-app.js)
// wipes it ON the switch. It cannot stop a request ISSUED in workspace A from
// writing its reply into workspace B after the switch has already run — and
// that is the harder half, because the result looks freshly fetched and the
// wipe has already happened, so nothing corrects it again. Every write that
// lands after an `await` on a workspace-scoped key needs this.
//
// DROPPING is right rather than rerouting or retrying: the payload describes a
// workspace the user is no longer looking at, and PHASE-1 of the switch has
// already restored the correct empty for the one they are. A refetch for the
// new workspace, where one is owed, is the switch tail's job (loadPrivateData
// PHASE-2), not this writer's.
//
// ⛔ CAPTURE BEFORE THE AWAIT. `sameWorkspaceWriter(root)` must be called on
// the SAME tick as the request, before the first `await` — it snapshots the
// workspace id at call time. Calling it inside the `.then()` snapshots the NEW
// workspace and the guard can never fire.
//
// Shipped first as a copy inside pages/admin/teams.js (workspace 27d4abdf2),
// copied a second time into tickets/components/TicketDetailModal.js
// (workspace ca5dcd758) whose note asked for exactly this extraction on the
// third use. The tickets copy stays where it is on purpose: the tickets
// package is a sub-app and must not import the shell — see
// __tests__/navSearchTenantScope.test.js's drift pin, which fails if the two
// implementations ever drift.

// Both id shapes are live on root: `workspace.id` from the SDK's workspace
// payload and `workspace._id` from the raw Mongo document. Stringified so a
// numeric id and its string form never read as two different tenants.
export const wsIdOf = (root) => String(root?.workspace?.id || root?.workspace?._id || '')

// Returns a `write(patch)` that applies the patch only while the workspace is
// still the one it was created in. Returns `true` when the patch landed and
// `false` when it was dropped, so a caller can tell the two apart (a silent
// no-op reads as success at the call site).
export const sameWorkspaceWriter = (root) => {
  // Keep the registered function self-contained when Frank revives its source.
  const idOf = (state) => String(state?.workspace?.id || state?.workspace?._id || '')
  const issuedFor = idOf(root)
  return (patch) => {
    if (idOf(root) !== issuedFor) return false
    root.update(patch)
    return true
  }
}
